import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import type { ApiClient, AuftragDetail as Detail } from '../api';
import {
  ABBRUCHGRUND_HOECHSTLAENGE, ABBRUCHGRUND_STANDARD, StatusText, auftragZustand,
} from '../api';
import { auftragssteuerung } from '../auftragsstatus';
import { artefaktArtText } from '../ifcDatenkern';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ConfirmDialog, DataTable, ErrorState, Feld, LoadingState,
  StatusBadge, type Spalte, type Zustand,
} from '../muster';

/**
 * Datum und Uhrzeit, oder ein Gedankenstrich.
 *
 * "Invalid Date" ist als Anzeige schlimmer als nichts - es sieht nach einem
 * Fehler in den Daten aus, war aber ein Fehler im Zugriff. Diese Helfer machen
 * den Unterschied sichtbar, ohne zu erschrecken.
 */
function Zeitpunkt(wert?: string | null): string {
  if (!wert) return '—';
  const d = new Date(wert);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('de-DE');
}

function Uhrzeit(wert?: string | null): string {
  if (!wert) return '—';
  const d = new Date(wert);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleTimeString('de-DE');
}

type Teilauftrag = NonNullable<Detail['parts']>[number];
type Ergebnis = NonNullable<Detail['artifacts']>[number];
type Ereignis = NonNullable<Detail['events']>[number];

export function Auftragsdetail({ api }: { api: ApiClient }) {
  const { id } = useParams<{ id: string }>();
  const [auftrag, setAuftrag] = useState<Detail | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);

  const laden = useCallback(async (aktiv: () => boolean) => {
    if (!id) return;
    try {
      const daten = await api.auftrag(id);
      if (aktiv()) { setAuftrag(daten); setFehler(null); }
    } catch (e) {
      if (aktiv()) setFehler(e);
    }
  }, [api, id]);

  useEffect(() => {
    if (!id) return undefined;
    let aktiv = true;
    const istAktiv = () => aktiv;

    laden(istAktiv);
    const takt = setInterval(() => laden(istAktiv), 10_000);

    return () => { aktiv = false; clearInterval(takt); };
  }, [id, laden]);

  const teilSpalten: Spalte<Teilauftrag>[] = useMemo(() => [
    {
      schluessel: 'modell', titel: 'Modell', immerSichtbar: true,
      wert: (p) => p.modelName,
    },
    { schluessel: 'art', titel: 'Art', wert: (p) => p.kind },
    {
      schluessel: 'status', titel: 'Status', art: 'status', breite: '150px',
      wert: (p) => StatusText[p.status] ?? p.status,
      inhalt: (p) => (
        <StatusBadge
          zustand={auftragZustand(p.status)}
          text={StatusText[p.status] ?? p.status}
          klein
        />
      ),
    },
    {
      // Der Arbeitsplatz und sein Fortschritt -- leer, solange der Teilauftrag
      // auf einer Maschine läuft oder noch nichts gemeldet wurde. Das ist der
      // Normalfall, kein Fehler, und wird deshalb auch nicht als einer
      // dargestellt.
      schluessel: 'arbeitsplatz', titel: 'Arbeitsplatz',
      wert: (p) => p.arbeitsplatzName ?? '',
      inhalt: (p) => (p.arbeitsplatzName ? (
        <span className="schwach">
          <strong>{p.arbeitsplatzName}</strong>
          {p.arbeitsplatzFortschritt && (
            <div>
              {p.arbeitsplatzFortschritt}
              {p.arbeitsplatzFortschrittSchrittVon !== null
                && p.arbeitsplatzFortschrittSchrittBis !== null
                && ` (${p.arbeitsplatzFortschrittSchrittVon} von `
                + `${p.arbeitsplatzFortschrittSchrittBis})`}
            </div>
          )}
        </span>
      ) : <span className="sehr-schwach">—</span>),
    },
    {
      schluessel: 'grund', titel: 'Grund',
      wert: (p) => p.failureReason ?? '',
      inhalt: (p) => (p.failureReason
        ? <span className="schwach">{p.failureReason}</span>
        : <span className="sehr-schwach">—</span>),
    },
  ], []);

  const kopfId = auftrag?.summary.id;

  const ergebnisSpalten: Spalte<Ergebnis>[] = useMemo(() => [
    {
      schluessel: 'datei', titel: 'Datei', immerSichtbar: true,
      wert: (d) => d.fileName,
    },
    // IFC-3d: ifc-datenkern, ifc-bericht und das Paket mit lesbarem Namen.
    { schluessel: 'art', titel: 'Art', wert: (d) => artefaktArtText(d.kind) },
    {
      schluessel: 'groesse', titel: 'Größe', art: 'zahl', einheit: 'MB',
      nachkomma: 1, breite: '120px',
      wert: (d) => d.sizeBytes / 1048576,
    },
    {
      schluessel: 'laden', titel: '', breite: '140px', sortierbar: false,
      ausrichtung: 'rechts',
      inhalt: (d) => (
        <button
          className="m-knopf"
          onClick={(e) => {
            e.stopPropagation();
            if (!kopfId) return;
            api.dateiLaden(
              `/api/jobs/${kopfId}/artifacts/${d.id}/download`, d.fileName,
            );
          }}
        >
          Herunterladen
        </button>
      ),
    },
  ], [api, kopfId]);

  const ereignisSpalten: Spalte<Ereignis>[] = useMemo(() => [
    {
      schluessel: 'zeit', titel: 'Zeit', breite: '110px',
      wert: (e) => e.at,
      inhalt: (e) => <span className="sehr-schwach zahlen">{Uhrzeit(e.at)}</span>,
    },
    {
      schluessel: 'stufe', titel: 'Stufe', art: 'status', breite: '170px',
      wert: (e) => `${e.level} ${e.code}`,
      inhalt: (e) => (
        <StatusBadge zustand={ereignisZustand(e.level)} text={e.code || e.level} klein
                     titel={e.level} />
      ),
    },
    { schluessel: 'meldung', titel: 'Meldung', wert: (e) => e.message },
  ], []);

  if (fehler && !auftrag) {
    return (
      <>
        <p><Link to="/auftraege">← Alle Aufträge</Link></p>
        <PageHeader titel="Auftrag" />
        <ErrorState
          meldung="Der Auftrag konnte nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={() => { setFehler(null); laden(() => true); }}
        />
      </>
    );
  }

  if (!auftrag) {
    return (
      <>
        <p><Link to="/auftraege">← Alle Aufträge</Link></p>
        <PageHeader titel="Auftrag" beschreibung="Teilaufträge, Ergebnisse und Protokoll." />
        <LoadingState text="Auftrag wird geladen …" zeilen={6} spalten={4} />
      </>
    );
  }

  // Die Kopfdaten liegen in der Detailantwort unter summary. Ein Kurzname statt
  // ueberall summary. davorzuschreiben - das laedt zum Verwechseln ein.
  const kopf = auftrag.summary;

  return (
    <>
      <p><Link to="/auftraege">← Alle Aufträge</Link></p>

      <Steuerung
        kopf={kopf}
        parts={auftrag.parts}
        api={api}
        kopfzeile={(knoepfe) => (
          <PageHeader
            titel={kopf.jobNumber}
            beschreibung={kopf.workload ?? undefined}
            meta={(
              <>
                <StatusBadge
                  zustand={auftragZustand(kopf.status)}
                  text={StatusText[String(kopf.status)] ?? String(kopf.status)}
                  klein
                />
                <div>
                  Beauftragt {Zeitpunkt(kopf.createdAt)}
                  {kopf.requestedByEmail && ` von ${kopf.requestedByEmail}`}
                </div>
              </>
            )}
            aktionen={knoepfe}
          />
        )}
      />

      {auftrag.parts && auftrag.parts.length > 0 && (
        <DataTable
            name="Teilaufträge"
            spalten={teilSpalten}
            zeilen={auftrag.parts}
            schluessel={(p) => p.id}
            suche={auftrag.parts.length > 8}
            spaltenwahl={false}
        />
      )}

      {auftrag.artifacts && auftrag.artifacts.length > 0 && (
        <DataTable
            name="Ergebnisse"
            spalten={ergebnisSpalten}
            zeilen={auftrag.artifacts}
            schluessel={(d) => d.id}
            suche={auftrag.artifacts.length > 8}
            spaltenwahl={false}
        />
      )}

      {auftrag.events && auftrag.events.length > 0 && (
        <DataTable
            name="Protokoll"
            spalten={ereignisSpalten}
            zeilen={auftrag.events}
            schluessel={(e) => `${e.at}:${e.code}:${e.message}`}
            suchePlatzhalter="Meldung, Code …"
            ausfuhr
            dicht
        />
      )}
    </>
  );
}

function ereignisZustand(stufe: string): Zustand {
  if (stufe === 'Error') return 'fehler';
  if (stufe === 'Warning') return 'warnung';
  return 'neutral';
}

/**
 * Pausieren, fortsetzen, abbrechen und löschen.
 *
 * Pausieren und Löschen wirken nur in der Warteschlange. Ein Teilauftrag, den eine
 * Maschine schon übernommen hat, läuft dort weiter — Revit hält nicht an, weil hier
 * ein Kennzeichen gesetzt wird. Deshalb sind die Knöpfe dann gesperrt und sagen
 * warum, statt eine Wirkung zu behaupten, die es nicht gibt.
 *
 * Die Knöpfe gehören nach Abschnitt 8 in den Seitenkopf, die Begründungen
 * darunter. Deshalb reicht die Komponente ihre Knöpfe über `kopfzeile` nach
 * oben durch, statt sie selbst irgendwo zu zeichnen.
 *
 * Wann welcher Knopf steht, entscheidet `auftragssteuerung` mit den Regeln des
 * Servers (AUF1). Bis zum 20.10.2026 entschied hier `Number(kopf.status) < 50` -
 * die Detailantwort nennt den Status aber beim Namen („Queued“), und `NaN < 50`
 * ist falsch: Die Steuerung stand bei keinem Auftrag.
 */
function Steuerung({
  kopf, parts, api, kopfzeile,
}: {
  kopf: Detail['summary'];
  parts: Detail['parts'];
  api: ApiClient;
  kopfzeile: (knoepfe: React.ReactNode) => React.ReactNode;
}) {
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [frage, setFrage] = useState<'abbruch' | 'loeschen' | null>(null);
  const [dialogFehler, setDialogFehler] = useState<unknown>(null);
  // Der Grund des Abbruchs (AUF3) - freiwillig; steht danach am Auftrag und im
  // Ereignis, ohne Angabe der Standardtext des Servers.
  const [grund, setGrund] = useState('');
  const grundZuLang = grund.trim().length > ABBRUCHGRUND_HOECHSTLAENGE;
  const navigate = useNavigate();

  // Ein Teilauftrag in Verarbeitung ist weder Queued noch beendet - dieselbe
  // Regel, mit der der Server Pausieren und Löschen ablehnt.
  const s = auftragssteuerung(kopf, parts);
  const { zeigen, wirdVerarbeitet } = s;

  const tun = async (was: string, aufruf: () => Promise<unknown>) => {
    setLaeuft(was);
    setMeldung(null);
    try {
      const a = await aufruf();
      setMeldung(a && typeof a === 'object' && 'message' in a
        ? String((a as { message: unknown }).message)
        : 'Erledigt.');
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  };

  const knoepfe = zeigen ? (
    <>
      {kopf.istPausiert ? (
        <button
          className="m-knopf m-knopf-haupt"
          disabled={laeuft !== null || !s.fortsetzen}
          onClick={() => tun('fort', () => api.auftragFortsetzen(kopf.id))}
        >
          {laeuft === 'fort' ? '…' : 'Fortsetzen'}
        </button>
      ) : (
        <button
          className="m-knopf"
          disabled={laeuft !== null || !s.pausieren}
          onClick={() => tun('pause', () => api.auftragPausieren(kopf.id))}
          title={wirdVerarbeitet
            ? 'Die Verarbeitung läuft schon — Pausieren wirkt nur in der '
              + 'Warteschlange'
            : 'Der Auftrag bleibt in der Warteschlange, wird aber nicht vergeben'}
        >
          {laeuft === 'pause' ? '…' : 'Pausieren'}
        </button>
      )}

      <button
        className="m-knopf"
        disabled={laeuft !== null || !s.abbrechen}
        onClick={() => { setDialogFehler(null); setGrund(''); setFrage('abbruch'); }}
        title={'Wirkt nicht sofort: Der Agent bemerkt es erst bei der nächsten '
          + 'Verlängerung seiner Zuweisung'}
      >
        Abbrechen
      </button>

      <button
        className="m-knopf m-knopf-gefahr"
        disabled={laeuft !== null || !s.loeschen}
        onClick={() => { setDialogFehler(null); setFrage('loeschen'); }}
        title={wirdVerarbeitet
          ? 'Die Verarbeitung läuft — Löschen würde die Maschine mit einem '
            + 'Ergebnis zurücklassen, das niemandem gehört'
          : 'Aus der Warteschlange entfernen'}
      >
        Löschen
      </button>
    </>
  ) : null;

  return (
    <>
      {kopfzeile(knoepfe)}

      {zeigen && kopf.istPausiert && (
        <div className="hinweis">
          <div>
            <strong>Pausiert</strong>
            {kopf.pausiertAm && ` seit ${Zeitpunkt(kopf.pausiertAm)}`}
            {kopf.pausiertVon && ` von ${kopf.pausiertVon}`}.
            <div style={{ fontSize: 12, marginTop: 3 }}>
              Der Auftrag bleibt in der Warteschlange, wird aber nicht vergeben.
            </div>
          </div>
        </div>
      )}

      {zeigen && wirdVerarbeitet && !kopf.istPausiert && (
        <p className="sehr-schwach" style={{ fontSize: 11 }}>
          Die Verarbeitung läuft. Pausieren und Löschen wirken nur in der
          Warteschlange — auf der Maschine läuft Revit weiter.
        </p>
      )}

      {meldung && <div className="hinweis">{meldung}</div>}

      <ConfirmDialog
        offen={frage === 'abbruch'}
        art="warnung"
        titel={`${kopf.jobNumber} abbrechen?`}
        text={'Der Abbruch wirkt nicht sofort: Der Agent bemerkt ihn erst bei der '
          + 'nächsten Verlängerung seiner Zuweisung.'}
        bestaetigenText="Abbrechen erzwingen"
        abbrechenText="Weiterlaufen lassen"
        laeuft={laeuft === 'abbruch'}
        fehler={dialogFehler}
        beiBestaetigen={async () => {
          if (grundZuLang) return;
          setLaeuft('abbruch');
          setDialogFehler(null);
          try {
            await api.auftragAbbrechen(kopf.id, grund);
            setFrage(null);
            setMeldung('Abbruch vorgemerkt.');
          } catch (e) {
            setDialogFehler(e);
          } finally {
            setLaeuft(null);
          }
        }}
        beiAbbrechen={() => setFrage(null)}
      >
        <Feld
          art="textfeld"
          beschriftung="Grund (freiwillig)"
          wert={grund}
          beiAenderung={(w) => setGrund(typeof w === 'string' ? w : '')}
          zeilen={2}
          breite="voll"
          deaktiviert={laeuft === 'abbruch'}
          hinweis={'Steht danach am Auftrag und im Ereignisprotokoll. Ohne Angabe: '
            + `„${ABBRUCHGRUND_STANDARD}“.`}
          fehler={grundZuLang
            ? `Höchstens ${ABBRUCHGRUND_HOECHSTLAENGE} Zeichen (jetzt ${grund.trim().length}).`
            : undefined}
        />
      </ConfirmDialog>

      <ConfirmDialog
        offen={frage === 'loeschen'}
        art="loeschen"
        titel={`${kopf.jobNumber} endgültig löschen?`}
        text="Der Auftrag und seine Ereignisse verschwinden."
        laeuft={laeuft === 'loeschen'}
        fehler={dialogFehler}
        beiBestaetigen={async () => {
          setLaeuft('loeschen');
          setDialogFehler(null);
          try {
            await api.auftragLoeschen(kopf.id);
            navigate('/auftraege');
          } catch (e) {
            setDialogFehler(e);
            setLaeuft(null);
          }
        }}
        beiAbbrechen={() => setFrage(null)}
      />
    </>
  );
}
